<template>
  <div>
    <menu-content-container :padding-left-right="8">
      <FontSizeColor
        :data-testid="`loop-text-settings`"
        no-border
        :block="block"
        :block-index="blockIndex"
        :col-index="colIndex"
      />
    </menu-content-container>

    <menu-content-container :padding-left-right="8">
      <TLabel
        :changed="isChanged('align')"
        :tooltip="$t('forms.reset_value')"
        @reset="resetChildAttr('align')"
        >{{ $t("forms.image_align") }}</TLabel
      >
      <TButtonGroup style="margin-top: 4px">
        <TGroupButton
          data="left"
          data-testid="align-left"
          small
          :active="align === 'left'"
          @button-clicked="() => (align = 'left')"
        >
          <AligmentLeft />
        </TGroupButton>
        <TGroupButton
          data="center"
          data-testid="align-center"
          small
          :active="align === 'center'"
          @button-clicked="() => (align = 'center')"
        >
          <AligmentCenter />
        </TGroupButton>
        <TGroupButton
          data="right"
          data-testid="align-right"
          small
          :active="align === 'right'"
          @button-clicked="() => (align = 'right')"
        >
          <AligmentRight />
        </TGroupButton>
      </TButtonGroup>
    </menu-content-container>

    <paddingForm
      data-testid="loop-text-padding"
      :padding-left-right="8"
      class="!mb-2"
      :border-bottom="false"
      external-usage
      :external-padding="padding"
      :changed="isChanged('padding')"
      :default-value="getChildDefault('padding')"
      @paddingChanged="onPaddingChanged"
      @reset="resetChildAttr('padding')"
    ></paddingForm>
  </div>
</template>

<script lang="ts" setup>
  import paddingForm from "../paddingForm.vue";
  import FontSizeColor from "./fontSizeColorLoop.vue";
  import { TopolLoopText } from "@/schemas/template/blocks/loopBlockSchema";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { normalizeSpacing } from "@/utils/spacingUtils";
  import { TButtonGroup, TGroupButton, TLabel } from "@topol/twig-ui";
  import { AligmentCenter, AligmentLeft, AligmentRight } from "@topol/icons";
  import { computed } from "vue";

  const mjmlStore = useMjmlStore();

  const props = defineProps<{
    colIndex: number;
    blockIndex: number;
    block: TopolLoopText;
  }>();

  // --- Reset helpers ---
  function getChildDefault(prop: string) {
    const baseValues = mjmlStore.getBaseAttrWithDefault("mj-text", prop);
    return baseValues?.value ?? baseValues?.defaultValue;
  }

  function isChanged(prop: string) {
    const currentValue =
      props.block.attributes[prop as keyof typeof props.block.attributes];
    const defaultValue = getChildDefault(prop);
    if (currentValue === undefined || defaultValue === undefined) return false;
    if (typeof currentValue === "string" && typeof defaultValue === "string") {
      const normCurrent = normalizeSpacing(currentValue);
      const normDefault = normalizeSpacing(defaultValue);
      if (normCurrent && normDefault) return normCurrent !== normDefault;
    }
    return String(currentValue) !== String(defaultValue);
  }

  function resetChildAttr(prop: string) {
    const defaultValue = getChildDefault(prop);
    mjmlStore.updateLoopBlockChildAttribute({
      columnIndex: props.colIndex,
      blockIndex: props.blockIndex,
      prop,
      value: defaultValue,
    });
  }

  const align = computed({
    get() {
      return props.block.attributes.align || "left";
    },
    set(val: string) {
      mjmlStore.updateLoopBlockChildAttribute({
        columnIndex: props.colIndex,
        blockIndex: props.blockIndex,
        prop: "align",
        value: val,
      });
    },
  });

  const padding = computed({
    get() {
      return props.block.attributes.padding || "0px";
    },
    set(val: string) {
      mjmlStore.updateLoopBlockChildAttribute({
        columnIndex: props.colIndex,
        blockIndex: props.blockIndex,
        prop: "padding",
        value: val,
      });
    },
  });

  function onPaddingChanged(val: string) {
    padding.value = val;
  }
</script>
